import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuctionStore } from '../stores/auction';
import { AUCTION_CATEGORIES, type AuctionCategory } from '@auction/shared';
import Icon from '../components/Icon';

export default function CreateAuctionPage() {
  const navigate = useNavigate();
  const { createAuction } = useAuctionStore();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other' as AuctionCategory,
    startingPrice: '',
    reservePrice: '',
    duration: '60', // minutes
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const startTime = new Date();
      const endTime = new Date(startTime.getTime() + parseInt(formData.duration) * 60 * 1000);

      await createAuction({
        title: formData.title,
        description: formData.description,
        category: formData.category,
        startingPrice: Math.round(parseFloat(formData.startingPrice) * 100),
        reservePrice: formData.reservePrice 
          ? Math.round(parseFloat(formData.reservePrice) * 100) 
          : null,
        startTime,
        endTime,
      });

      navigate('/');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Icon name="add_circle" className="text-primary-600" />
        Create Auction
      </h1>

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <Icon name="title" size="small" className="text-gray-400" />
            Title *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="input"
            placeholder="Vintage Watch Collection"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <Icon name="description" size="small" className="text-gray-400" />
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="input min-h-[100px]"
            placeholder="Describe your item..."
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <Icon name="category" size="small" className="text-gray-400" />
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as AuctionCategory })}
            className="input"
          >
            {AUCTION_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Icon name="sell" size="small" className="text-gray-400" />
              Starting Price (£) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              value={formData.startingPrice}
              onChange={(e) => setFormData({ ...formData, startingPrice: e.target.value })}
              className="input"
              placeholder="10.00"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <Icon name="lock" size="small" className="text-gray-400" />
              Reserve Price (£)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.reservePrice}
              onChange={(e) => setFormData({ ...formData, reservePrice: e.target.value })}
              className="input"
              placeholder="Optional"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <Icon name="schedule" size="small" className="text-gray-400" />
            Duration
          </label>
          <select
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            className="input"
          >
            <option value="5">5 minutes (demo)</option>
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="60">1 hour</option>
            <option value="180">3 hours</option>
            <option value="1440">24 hours</option>
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <Icon name="error" size="small" />
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn-secondary flex items-center gap-1"
          >
            <Icon name="close" size="small" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center gap-1"
          >
            {loading ? (
              <>
                <Icon name="progress_activity" size="small" className="animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Icon name="add" size="small" />
                Create Auction
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
